import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { checkRankUp } from '../utils/points';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Component for displaying single or multiple images
const ImageGallery = ({ imageUrl, imageUrls, system }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Use imageUrls if available, otherwise fall back to single imageUrl
  const images = imageUrls?.length > 0 ? imageUrls : (imageUrl ? [imageUrl] : []);
  
  if (images.length === 0) return null;
  
  const isMultiple = images.length > 1;
  const isDermaOrPath = ['Dermatología', 'Patología'].includes(system);
  
  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  
  const getImageLabel = (index) => {
    if (system === 'Dermatología') {
      const labels = ['Vista general', 'Acercamiento', 'Dermatoscopia'];
      return labels[index] || `Imagen ${index + 1}`;
    }
    if (system === 'Patología') {
      const labels = ['Panorámica (4x)', 'Intermedio (10x)', 'Detalle (40x)'];
      return labels[index] || `Imagen ${index + 1}`;
    }
    return `Imagen ${index + 1}`;
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-black">
          {isDermaOrPath 
            ? (system === 'Dermatología' ? 'Imágenes Dermatológicas' : 'Imágenes Histopatológicas')
            : 'Imagen Radiológica'
          }
        </h3>
        {isMultiple && (
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {currentIndex + 1} / {images.length}
          </span>
        )}
      </div>
      
      <div className="relative">
        {/* Main Image */}
        <div className="flex justify-center">
          <img
            src={images[currentIndex]}
            alt={getImageLabel(currentIndex)}
            className="max-w-full h-auto rounded-lg shadow-md"
            style={{ maxHeight: '500px' }}
          />
        </div>
        
        {/* Navigation Arrows */}
        {isMultiple && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>
      
      {/* Image Label */}
      {isMultiple && (
        <p className="text-center text-sm text-gray-600 mt-3 font-medium">
          {getImageLabel(currentIndex)}
        </p>
      )}
      
      {/* Thumbnail Navigation */}
      {isMultiple && (
        <div className="flex justify-center space-x-3 mt-4">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex 
                  ? 'border-black ring-2 ring-black ring-offset-2' 
                  : 'border-gray-300 hover:border-gray-500'
              }`}
            >
              <img
                src={img}
                alt={getImageLabel(idx)}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ImagenDXGame = ({ user, onLogout }) => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchCase();
  }, [caseId]);

  const fetchCase = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/imagendx/cases/${caseId}`);
      setCaseData(response.data);
    } catch (error) {
      console.error('Error fetching case:', error);
      toast.error('Error al cargar el caso');
      navigate('/imagen-dx');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedOption) {
      toast.error('Por favor selecciona una opción');
      return;
    }

    try {
      const response = await apiClient.post('/imagendx/submit', {
        case_id: caseId,
        selected_option: selectedOption
      });
      setResult(response.data);
      checkRankUp(response.data);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Error al enviar respuesta');
    }
  };

  const handleRetry = () => {
    setSelectedOption(null);
    setSubmitted(false);
    setResult(null);
  };

  const handleBackToCases = () => {
    if (caseData?.system) {
      navigate(`/imagen-dx/subtemas/${encodeURIComponent(caseData.system)}`);
    } else {
      navigate('/imagen-dx');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!caseData) {
    return null;
  }

  // Screen 1: Question
  if (!submitted) {
    return (
      <div className="min-h-screen bg-white py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-black">Imagen DX</h1>
                <p className="text-gray-500">{caseData.system} - {caseData.modality}</p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBackToCases}
                  className="px-4 py-2 text-gray-500 hover:text-black transition-colors"
                >
                  Salir
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Menu Principal
                </button>
              </div>
            </div>
          </div>

          {/* Image Section - Single or Multiple Images */}
          {(caseData.image_url || caseData.image_urls?.length > 0) && (
            <ImageGallery 
              imageUrl={caseData.image_url}
              imageUrls={caseData.image_urls}
              system={caseData.system}
            />
          )}

          {/* Clinical Case */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-black mb-4">Caso Clinico</h3>
            <p className="text-gray-700 leading-relaxed">{caseData.caso_clinico}</p>
          </div>

          {/* Question */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-black mb-6">
              ¿Cuál es el hallazgo o signo radiológico presente?
            </h3>
            
            <div className="space-y-4">
              {['A', 'B', 'C', 'D'].map((option) => {
                const optionText = caseData[`option_${option.toLowerCase()}`];
                return (
                  <button
                    key={option}
                    onClick={() => setSelectedOption(option)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedOption === option
                        ? 'border-black bg-gray-50'
                        : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-bold mr-3">
                        {option}
                      </span>
                      <span className="text-gray-800 pt-1">{optionText}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              onClick={handleSubmit}
              disabled={!selectedOption}
              className="w-full mt-6 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all font-semibold text-lg"
            >
              Enviar Respuesta
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Screen 2: Feedback
  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Result Card */}
        <div className={`bg-white rounded-xl overflow-hidden mb-6 border ${
          result.is_correct ? 'border-gray-200' : 'border-gray-200'
        }`}>
          {/* Header with Radiologist and Case Image */}
          <div className="p-8 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Radiologist Image */}
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Retroalimentacion del Radiologo</h3>
                <div className="flex justify-center mb-4">
                  <img
                    src={result.is_correct ? '/radio_feliz.avif' : '/radio_enojado.jpg'}
                    alt={result.is_correct ? 'Radiólogo feliz' : 'Radiólogo enojado'}
                    className="w-64 h-64 rounded-full object-cover shadow-lg"
                  />
                </div>
                <h2 className={`text-3xl font-bold mb-2 ${
                  result.is_correct ? 'text-black' : 'text-black'
                }`}>
                  {result.is_correct ? 'Correcto' : 'Incorrecto'}
                </h2>
                <p className="text-lg text-gray-500">
                  {result.is_correct
                    ? 'Excelente diagnostico'
                    : 'Sigue estudiando, lo haras mejor la proxima vez'}
                </p>
              </div>

              {/* Case Image */}
              {caseData.image_url && (
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Imagen del Caso</h3>
                  <div className="flex justify-center">
                    <img
                      src={caseData.image_url}
                      alt="Imagen radiológica del caso"
                      className="max-w-full h-auto rounded-lg shadow-lg"
                      style={{ maxHeight: '320px' }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Feedback Details */}
          <div className="p-8">
            {!result.is_correct && (
              <div className="mb-6 p-4 bg-gray-50 border-l-4 border-black rounded">
                <h4 className="font-semibold text-black mb-2">Respuesta correcta:</h4>
                <p className="text-gray-700">{result.correct_answer}</p>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-black mb-2">Modalidad:</h4>
                <p className="text-gray-700">{result.feedback.modality}</p>
              </div>

              <div>
                <h4 className="font-semibold text-black mb-2">Interpretacion Clave:</h4>
                <p className="text-gray-700">{result.feedback.interpretation_clave}</p>
              </div>

              <div>
                <h4 className="font-semibold text-black mb-2">Diagnostico Sugerido:</h4>
                <p className="text-gray-700">{result.feedback.diagnostico_sugerido}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-4">
          <button
            onClick={handleRetry}
            className="flex-1 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-all font-semibold"
          >
            Intentar de Nuevo
          </button>
          <button
            onClick={handleBackToCases}
            className="flex-1 px-6 py-3 bg-white text-black rounded-lg border-2 border-black hover:bg-gray-100 transition-all font-semibold"
          >
            Volver a Casos
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex-1 px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 transition-all font-semibold"
          >
            Menu Principal
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImagenDXGame;